import React, { FC, useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import {
  AccordionSummary,
  AccordionDetails,
  Box,
  Container,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import styled from 'styled-components';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { CircularProgressMolecule } from "../components/molecules/CircularProgressMolecule";
import { VisualizedMenus } from "../components/VisualizedMenus";
import { Toolbar } from "../components/FooterToolbar";
import { FeedbackBar } from "../components/molecules/FeedbackBar";
import {
  getTrainingIndicator,
  getTrainingIndicatorDataRef,
  useAuth0Token
} from "../common/http-requests";
import {
  trainingIndicatorResponseType,
  trainingIndicatorDataRefRequestType,
  trainingIndicatorDataRefResponseType
} from "../common/type";
import { formatSemester } from "../common/util";
import { themeColors } from "../common/colors";
import { useUserId } from "../hooks/useStoreGetters";

const BAR_CHART_SIZE: number[] = [
  1900, 320
];

const TABLE_HEAD_FIRST_ROW_HEIGHT = 69;
const MEXT_TABLE_HEAD_SECOND_ROW_HEIGHT = 69;
const TOKUSHIMA_TABLE_HEAD_SECOND_ROW_HEIGHT = 37;

// Material-UIのスタイル設定
const useStyles = makeStyles({
  chartContainer: {
    position: 'relative',
    marginTop: '60px',
    padding: '20px',
    overflow: 'hidden'
  },
  tableHeadStyle: {
    textAlign: "center",
    borderRight: "solid 1px #fff",
    lineHeight: "1rem",
    padding: "10px 2px",
  },
  // mextTableHeadSecondRowStyleおよびtokushimaTableHeadSecondRowStyleは、
  // stickyHeaderに対応するため。高さが固定でないとヘッダーが崩れてしまうため
  // tableHeadStyleを使っているところは、動作を確認して画面幅を変更しても高さが変わらないため指定しない
  mextTableHeadSecondRowStyle: {
    textAlign: "center",
    borderRight: "solid 1px #fff",
    lineHeight: "1rem",
    padding: "0 2px",
    height: `${MEXT_TABLE_HEAD_SECOND_ROW_HEIGHT}px`,
    minWidth: "101px",
  },
  tokushimaTableHeadSecondRowStyle: {
    textAlign: "center",
    borderRight: "solid 1px #fff",
    lineHeight: "1rem",
    padding: "0 2px",
    height: `${TOKUSHIMA_TABLE_HEAD_SECOND_ROW_HEIGHT}px`,
    minWidth: "95px",
  },
  tableCellStyle: {
    position: 'relative',
    padding: "0 2px",
    textAlign: 'center',
    overflow: "hidden",
  },
  tableCellPsStyle: {
    border: 'solid 1px #bbcddd',
    boxSizing: 'border-box',
  },
  tableCellVertical: {
    writingMode: 'vertical-rl',
    textOrientation: 'upright',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignContent: 'center',
    width: '100%',
    height: '15%',
  },
  tableCellRefStyle: {
    border: 'solid 1px #e2e2e2',
    boxSizing: 'border-box',
  },
});

const AccordionLabel = styled(Box)(() => ({
  position: "relative",
  width: '100%',
  height: 'auto',
  maxWidth: `${BAR_CHART_SIZE[0]}px`,
  margin: '8px auto 0',
  borderTop: `solid 1px ${themeColors[0]}`,
  borderBottom: `solid 1px ${themeColors[0]}`,
  color: `${themeColors[0]}`,
}));

const DefaultCreditDataRef: trainingIndicatorDataRefResponseType = {
  data_ref: [],
};

const TrainingIndicatorDataRefPage: FC = () => {
  const { t } = useTranslation();
  const [isSending, setIsSending] = useState(true);
  const { getToken } = useAuth0Token();
  const userId = useUserId();
  const classes = useStyles();
  const [tiMextDataRefData, setTIMextDataRefData] = useState<trainingIndicatorDataRefResponseType>(DefaultCreditDataRef);
  const [tiTokushimaDataRefData, setTITokushimaDataRefData] = useState<trainingIndicatorDataRefResponseType>(DefaultCreditDataRef);
  const [tiMextData, setTIMextData] = useState<number[]>([]);
  const [tiTokushimaData, setTITokushimaData] = useState<number[]>([]);
  const [expandedNationwide, setExpandedNationwide] = useState(true);
  const [expandedTokushima, setExpandedTokushima] = useState(true);
  const toggleNationwide = () => setExpandedNationwide(!expandedNationwide);
  const toggleTokushima = () => setExpandedTokushima(!expandedTokushima);

  const mextHumanDevelopmentIndicatorList = Array.from({ length: 17 }, (_, i) => ({
    id: String(i + 1).padStart(2, "0"),
    label: t(`indicator.mext_${i + 1}`)
  }));
  const mhdi = mextHumanDevelopmentIndicatorList;

  const tokushimaHumanDevelopmentIndicatorList = Array.from({ length: 20 }, (_, i) => ({
    id: String(i + 1).padStart(2, "0"),
    label: t(`indicator.tokushima_${i + 1}`)
  }));
  const thdi = tokushimaHumanDevelopmentIndicatorList;

  const tableHeadListCS = [
    { label: t("common.period"), width: "4vw", color: "white", backgroundColor: themeColors[3] },
    { label: t("common.semester"), width: "4vw", color: "white", backgroundColor: themeColors[3] },
    { label: t("common.subjectName"), width: "20vw", color: "white", backgroundColor: themeColors[3] },
    { label: t("common.credit"), width: "3vw", color: "white", backgroundColor: themeColors[3] },
    { label: t("common.creditScore"), width: "4vw", color: "white", backgroundColor: themeColors[3] },
  ];

  const mextTableHeadListQuals = [
    { label: t("indicator.mext5_1"), width: "4vw", color: "white", colSpan: 5, backgroundColor: themeColors[0], indicators: [mhdi[0], mhdi[1], mhdi[2], mhdi[3], mhdi[4]] },
    { label: t("indicator.mext5_2"), width: "4vw", color: "white", colSpan: 4, backgroundColor: themeColors[0], indicators: [mhdi[5], mhdi[6], mhdi[7], mhdi[8]] },
    { label: t("indicator.mext5_3"), width: "4vw", color: "white", colSpan: 5, backgroundColor: themeColors[0], indicators: [mhdi[9], mhdi[10], mhdi[11], mhdi[12], mhdi[13]] },
    { label: t("indicator.mext5_4"), width: "4vw", color: "white", colSpan: 1, backgroundColor: themeColors[0], indicators: [mhdi[14]] },
    { label: t("indicator.mext5_5"), width: "4vw", color: "white", colSpan: 2, backgroundColor: themeColors[0], indicators: [mhdi[15], mhdi[16]] },
  ];

  const tokushimaTableHeadListQuals = [
    { label: t("indicator.tokushima5_1"), width: "4vw", color: "white", colSpan: 6, backgroundColor: themeColors[0], indicators: [thdi[0], thdi[1], thdi[2], thdi[3], thdi[4], thdi[5]] },
    { label: t("indicator.tokushima5_2"), width: "4vw", color: "white", colSpan: 4, backgroundColor: themeColors[0], indicators: [thdi[6], thdi[7], thdi[8], thdi[9]] },
    { label: t("indicator.tokushima5_3"), width: "4vw", color: "white", colSpan: 4, backgroundColor: themeColors[0], indicators: [thdi[10], thdi[11], thdi[12], thdi[13]] },
    { label: t("indicator.tokushima5_4"), width: "4vw", color: "white", colSpan: 4, backgroundColor: themeColors[0], indicators: [thdi[14], thdi[15], thdi[16], thdi[17]] },
    { label: t("indicator.tokushima5_5"), width: "4vw", color: "white", colSpan: 2, backgroundColor: themeColors[0], indicators: [thdi[18], thdi[19]] },
  ];

  // データ例
  //   "data_ref": [
  //     { 'earned_period': 2019, 'earned_semester_code': '1', 'subject_name': '日本国憲法', 'credit_score': 100, 'ps_credits': [1.0, ..., 0.0] },
  //     ...
  //     ]
  // const [trainingIndicatorDataRefData, setTrainingIndicatorDataRefData] = useState<trainingIndicatorDataRefResponseType>(DefaultCreditDataRef);

  // api取得失敗時のアラート
  const [feedbackInfo, setFeedbackInfo] = useState({
    open: false,
    text: "",
    type: "success" as "error" | "success" | "warning" | "info",
    autoHideDuration: null,
  });

  // clickawayした際、api取得失敗時のアラートを行わない。
  const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedbackInfo({ ...feedbackInfo, open: false });
  };

  // 以下は、記録一覧画面のコードを流用した。
  // Phase1と異なり、画面遷移が必要ではないため、location（uselocation）は使用しない。
  // React18(2022年)より、strictMode（javascriptのコードを通常より厳しくエラーチェックできるモード）において
  // オフスクリーンから再度表示されることで、useEffectが２回発火（キック）される仕様変更となっため、1回に制限するよう変更した。
  // また、以下のコードの})();の();は、定義した関数を即時実行している。
  // 最後にreturn文にて定義された関数は、クリーンアップ関数であり、リスナー、オブザーバー等のオブジェクトを設定した場合、
  // レンダリング時に毎回インスタンスを作成されるため、メモリーリーク及びメモリーの圧迫を抑止するための関数。
  // useEffectの依存配列（第2引数）は、通常、空リストもしくは、具体的な変数のリストを登録するのが一般的であり、
  // 定義しない場合は、FCのstate, propsのうちいずれかが更新される度に実行されてしまうため、通常、定義しないことはない。
  let alreadyFiredOnce = false;
  useEffect(() => {
    (async () => {
      if (!alreadyFiredOnce) {
        await getTrainingIndicatorData(userId);
      }
    })();
    return () => {
      alreadyFiredOnce = true;
    };
  }, []);

  const getTrainingIndicatorData = async (
    login_user_id: number,
  ) => {
    setIsSending(true);
    try {
      // 全国指標のデータを取得
      const requestMextParams: trainingIndicatorDataRefRequestType = {
        login_user_id,
        target_type: "mext",
      };
      const mextData: trainingIndicatorResponseType = await getTrainingIndicator(
        await getToken(),
        requestMextParams
      );
      const mextDataRefData: trainingIndicatorDataRefResponseType = await getTrainingIndicatorDataRef(
        await getToken(),
        requestMextParams
      );
      // 徳島県指標のデータを取得
      const requestTokushimaParams: trainingIndicatorDataRefRequestType = {
        login_user_id,
        target_type: "tokushima",
      };
      const tokushimaData: trainingIndicatorResponseType = await getTrainingIndicator(
        await getToken(),
        requestTokushimaParams
      );
      const tokushimaDataRefData: trainingIndicatorDataRefResponseType = await getTrainingIndicatorDataRef(
        await getToken(),
        requestTokushimaParams
      );

      // 文科省指標データをset
      setTIMextData(mextData.training_indicators);
      setTIMextDataRefData(mextDataRefData);
      // 徳島県指標データをset
      setTITokushimaData(tokushimaData.training_indicators);
      setTITokushimaDataRefData(tokushimaDataRefData);
      // setTrainingIndicatorDataRefData(data);
    } catch {
      setFeedbackInfo({
        ...feedbackInfo,
        open: true,
        text: t("msg.cannotLoadData"),
        type: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Container maxWidth="xl">
      {isSending ?
        <CircularProgressMolecule displayLabel={t("msg.loading")} /> :
        (
          <>
            <VisualizedMenus />
            {feedbackInfo.open &&
              <FeedbackBar
                feedbackInfo={feedbackInfo}
                handleClose={handleClose}
              ></FeedbackBar>
            }
            <Paper className={classes.chartContainer}>
              <AccordionLabel>
                <AccordionSummary
                  onClick={toggleNationwide}
                  expandIcon={expandedNationwide ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                  <Typography>{t("indicator.title_mext")}</Typography>
                </AccordionSummary>
              </AccordionLabel>
              {expandedNationwide && (
                <AccordionDetails>
                  <TableContainer sx={{ maxHeight: 700 }}>
                    <Table aria-label="simple table" stickyHeader>
                      <TableHead key="table-head">
                        <TableRow>
                          <TableCell
                            className={classes.tableHeadStyle}
                            sx={{ color: 'white', backgroundColor: themeColors[3] }}
                            colSpan={5}
                          >{t("common.subjectName")}</TableCell>
                          <TableCell sx={{ width: '2px', padding: '0' }} />
                          <TableCell
                            className={classes.tableHeadStyle}
                            sx={{ color: 'white', backgroundColor: themeColors[0] }}
                            colSpan={17}
                          >{t("indicatorDataRef.mext_1")}
                            <br />{t("indicatorDataRef.mext_2")}
                            <br />{t("indicatorDataRef.mext_3")}</TableCell>
                        </TableRow>
                        <TableRow>
                          {tableHeadListCS?.map((item: any, index: number) => (
                            <TableCell
                              key={`${item.label}-${index}`}
                              className={classes.tableHeadStyle}
                              sx={{
                                top: `${TABLE_HEAD_FIRST_ROW_HEIGHT}px`,
                                width: item.width,
                                color: item.color,
                                backgroundColor: item.backgroundColor
                              }}
                              rowSpan={2}
                            >{item.label}
                            </TableCell>
                          ))}
                          <TableCell sx={{ width: '2px', padding: '0' }} />
                          {mextTableHeadListQuals?.map((item: any, index: number) => (
                            <TableCell
                              key={`${item.label}-${index}`}
                              className={classes.mextTableHeadSecondRowStyle}
                              sx={{
                                top: `${TABLE_HEAD_FIRST_ROW_HEIGHT}px`,
                                width: item.width,
                                color: item.color,
                                backgroundColor: item.backgroundColor
                              }}
                              colSpan={item.colSpan}
                            >{item.label}
                            </TableCell>
                          ))}
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ width: '2px', padding: '0' }} />
                          {mextTableHeadListQuals?.map((quals: any, qualsindex: number) => (
                            quals.indicators?.map((item: any, index: number) => (
                              <TableCell
                                key={`${item.label}-${index}`}
                                className={classes.tableHeadStyle}
                                sx={{
                                  top: `${TABLE_HEAD_FIRST_ROW_HEIGHT + MEXT_TABLE_HEAD_SECOND_ROW_HEIGHT}px`,
                                  width: '1vw',
                                  color: quals.color,
                                  backgroundColor: quals.backgroundColor,
                                  fontSize: '.8rem'
                                }}
                              ><div className={classes.tableCellVertical}>{item.label}</div>
                              </TableCell>
                            ))
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody key="table-body">
                        {tiMextDataRefData.data_ref?.map((item: any, index: number) => (
                          <TableRow key={`table-body-row-${index}`}>
                            <React.Fragment key={`${item.subject_name}-${index}`}>
                              <TableCell className={`${classes.tableCellStyle} ${classes.tableCellRefStyle}`}>{item.earned_period}</TableCell>
                              <TableCell className={`${classes.tableCellStyle} ${classes.tableCellRefStyle}`}>{formatSemester(item.earned_semester_code, t)}</TableCell>
                              <TableCell className={`${classes.tableCellStyle} ${classes.tableCellRefStyle}`}>{item.subject_name}</TableCell>
                              <TableCell className={`${classes.tableCellStyle} ${classes.tableCellRefStyle}`}>{item.credit}</TableCell>
                              <TableCell className={`${classes.tableCellStyle} ${classes.tableCellRefStyle}`}>{item.credit_score ?? "－"}</TableCell>
                              <TableCell sx={{ width: '2px', padding: '0' }} />
                              {item.training_credits?.map((ps: any, psindex: number) => (
                                <TableCell
                                  key={`ps-${psindex}`}
                                  className={`${classes.tableCellStyle} ${classes.tableCellPsStyle}`}
                                  sx={{ color: themeColors[0] }}
                                >{(typeof ps === 'number' && ps !== 0) ? ps.toFixed(3) : ps}</TableCell>
                              ))}
                            </React.Fragment>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell
                            className={classes.tableHeadStyle}
                            sx={{ color: 'white', backgroundColor: themeColors[3] }}
                            colSpan={5}
                          >{t("indicatorDataRef.total")}</TableCell>
                          <TableCell sx={{ width: '2px', padding: '0' }} />
                          {tiMextData.map((num: number, index: number) => (
                            <TableCell
                              key={`table-body-cell-total-${index}`}
                              className={classes.tableHeadStyle}
                              sx={{ color: 'white', backgroundColor: themeColors[0] }}
                            >{num}</TableCell>
                          ))}
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              )}
              <AccordionLabel>
                <AccordionSummary
                  onClick={toggleTokushima}
                  expandIcon={expandedTokushima ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                  <Typography>{t("indicator.title_tokushima")}</Typography>
                </AccordionSummary>
              </AccordionLabel>
              {expandedTokushima && (
                <AccordionDetails>
                  <TableContainer sx={{ maxHeight: 700 }}>
                    <Table aria-label="simple table" stickyHeader>
                      <TableHead key="table-head">
                        <TableRow>
                          <TableCell
                            className={classes.tableHeadStyle}
                            sx={{ color: 'white', backgroundColor: themeColors[3] }}
                            colSpan={5}
                          >{t("common.subjectName")}</TableCell>
                          <TableCell sx={{ width: '2px', padding: '0' }} />
                          <TableCell
                            className={classes.tableHeadStyle}
                            sx={{ color: 'white', backgroundColor: themeColors[0] }}
                            colSpan={20}
                          >{t("indicatorDataRef.tokushima_1")}
                            <br />{t("indicatorDataRef.tokushima_2")}
                            <br />{t("indicatorDataRef.tokushima_3")}</TableCell>
                        </TableRow>
                        <TableRow>
                          {tableHeadListCS?.map((item: any, index: number) => (
                            <TableCell
                              key={`${item.label}-${index}`}
                              className={classes.tableHeadStyle}
                              sx={{
                                top: `${TABLE_HEAD_FIRST_ROW_HEIGHT}px`,
                                width: item.width,
                                color: item.color,
                                backgroundColor: item.backgroundColor
                              }}
                              rowSpan={2}
                            >{item.label}
                            </TableCell>
                          ))}
                          <TableCell sx={{ width: '2px', padding: '0' }} />
                          {tokushimaTableHeadListQuals?.map((item: any, index: number) => (
                            <TableCell
                              key={`${item.label}-${index}`}
                              className={classes.tokushimaTableHeadSecondRowStyle}
                              sx={{
                                top: `${TABLE_HEAD_FIRST_ROW_HEIGHT}px`,
                                width: item.width,
                                color: item.color,
                                backgroundColor: item.backgroundColor
                              }}
                              colSpan={item.colSpan}
                            >{item.label}
                            </TableCell>
                          ))}
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ width: '2px', padding: '0' }} />
                          {tokushimaTableHeadListQuals?.map((quals: any, qualsindex: number) => (
                            quals.indicators?.map((item: any, index: number) => (
                              <TableCell
                                key={`${item.label}-${index}`}
                                className={classes.tableHeadStyle}
                                sx={{
                                  top: `${TABLE_HEAD_FIRST_ROW_HEIGHT + TOKUSHIMA_TABLE_HEAD_SECOND_ROW_HEIGHT}px`,
                                  width: '1vw',
                                  color: quals.color,
                                  backgroundColor: quals.backgroundColor,
                                  fontSize: '.8rem'
                                }}
                              ><div className={classes.tableCellVertical}>{item.label}</div>
                              </TableCell>
                            ))
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody key="table-body">
                        {tiTokushimaDataRefData.data_ref?.map((item: any, index: number) => (
                          <TableRow key={`table-body-row-${index}`}>
                            <React.Fragment key={`${item.subject_name}-${index}`}>
                              <TableCell className={`${classes.tableCellStyle} ${classes.tableCellRefStyle}`}>{item.earned_period}</TableCell>
                              <TableCell className={`${classes.tableCellStyle} ${classes.tableCellRefStyle}`}>{formatSemester(item.earned_semester_code, t)}</TableCell>
                              <TableCell className={`${classes.tableCellStyle} ${classes.tableCellRefStyle}`}>{item.subject_name}</TableCell>
                              <TableCell className={`${classes.tableCellStyle} ${classes.tableCellRefStyle}`}>{item.credit}</TableCell>
                              <TableCell className={`${classes.tableCellStyle} ${classes.tableCellRefStyle}`}>{item.credit_score ?? "－"}</TableCell>
                              <TableCell sx={{ width: '2px', padding: '0' }} />
                              {item.training_credits?.map((ps: any, psindex: number) => (
                                <TableCell
                                  key={`ps-${psindex}`}
                                  className={`${classes.tableCellStyle} ${classes.tableCellPsStyle}`}
                                  sx={{ color: themeColors[0] }}
                                >{(typeof ps === 'number' && ps !== 0) ? ps.toFixed(3) : ps}</TableCell>
                              ))}
                            </React.Fragment>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell
                            className={classes.tableHeadStyle}
                            sx={{ color: 'white', backgroundColor: themeColors[3] }}
                            colSpan={5}
                          >{t("indicatorDataRef.total")}</TableCell>
                          <TableCell sx={{ width: '2px', padding: '0' }} />
                          {tiTokushimaData.map((num: number, index: number) => (
                            <TableCell
                              key={`table-body-cell-total-${index}`}
                              className={classes.tableHeadStyle}
                              sx={{ color: 'white', backgroundColor: themeColors[0] }}
                            >{num}</TableCell>
                          ))}
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              )}
            </Paper>
          </>
        )}
      <Toolbar />
    </Container>
  );
};

export default TrainingIndicatorDataRefPage;
